import { espoAppDB } from "./espoAppDB";

const CACHE_EXPIRY_TIME = 30 * 60 * 1000;

export const initCache = async (): Promise<any> => {
  return {
    match: async (request: Request) => {
      const url = request.url;
      const key = decodeURIComponent(url.split("/cache/")[1] || "");
      const data = await espoAppDB.get(key);
      if (!data) return undefined;
      return new Response(
        JSON.stringify({
          data,
          timestamp: Date.now(),
          expiry: Date.now() + CACHE_EXPIRY_TIME,
        }),
        {
          headers: { "Content-Type": "application/json" },
        },
      );
    },
    delete: async (request: Request) => {
      const url = request.url;
      const key = decodeURIComponent(url.split("/cache/")[1] || "");
      await espoAppDB.delete(key);
    },
    keys: async () => {
      const keys = await espoAppDB.getAllKeys();
      return keys.map(
        (key) => new Request(`/cache/${encodeURIComponent(key)}`),
      );
    },
  };
};

export const getFromCache = async <T>(key: string): Promise<T | null> => {
  return espoAppDB.get<T>(key);
};

export const setToCache = async <T>(
  key: string,
  data: T,
  expiryTime: number = CACHE_EXPIRY_TIME,
): Promise<void> => {
  await espoAppDB.set(key, data, expiryTime);
};

export const removeFromCache = async (key: string): Promise<void> => {
  await espoAppDB.delete(key);
};

export const clearCache = async (): Promise<void> => {
  try {
    await espoAppDB.clear();
  } catch (error) {
    console.error("Ошибка при очистке кэша:", error);
  }
};

export const cleanupOldCacheKeys = async (): Promise<void> => {
  try {
    if (typeof localStorage !== "undefined") {
      if (localStorage.getItem("projects")) {
        localStorage.removeItem("projects");
        console.log("Удален 'projects' из localStorage");
      }
      if (localStorage.getItem("metadata")) {
        localStorage.removeItem("metadata");
        console.log("Удален 'metadata' из localStorage");
      }
    }

    const keys = await espoAppDB.getAllKeys();

    const validKeys = [
      "tasks:list",
      "meetings:list",
      "projects:list",
      "opportunities:list",
      "metadata:app",
      "preload:metadata",
      "preload:users",
      "preload:statuses",
    ];

    let deletedCount = 0;
    for (const key of keys) {
      const isOldKeyWithParams =
        key.includes("/Task/list?") ||
        key.includes("/Meeting/list?") ||
        key.includes("/Project/list?") ||
        key.includes("/Opportunity?");

      const isSimpleKey = validKeys.includes(key);

      if (isOldKeyWithParams && !isSimpleKey) {
        await espoAppDB.delete(key);
        deletedCount++;
      }
    }

    if (deletedCount > 0) {
      console.log(`EspoAppDB: очищено ${deletedCount} старых ключей кэша`);
    }
  } catch (error) {
    console.error("Ошибка при очистке старых ключей кэша:", error);
  }
};

export const getCacheKeys = async (): Promise<string[]> => {
  try {
    return await espoAppDB.getAllKeys();
  } catch (error) {
    console.error("Ошибка при получении ключей кэша:", error);
    return [];
  }
};

export const hasInCache = async (key: string): Promise<boolean> => {
  return espoAppDB.has(key);
};

export const generateCacheKey = (
  endpoint: string,
  params?: Record<string, any>,
): string => {
  const baseKey = endpoint;
  if (!params || Object.keys(params).length === 0) {
    return baseKey;
  }

  const paramsString = JSON.stringify(params);
  return `${baseKey}?${btoa(paramsString)}`;
};

const isNetworkAvailable = (): boolean => {
  return typeof navigator !== "undefined" && navigator.onLine;
};

export const networkFirst = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
  expiryTime?: number,
): Promise<T> => {
  if (!isNetworkAvailable()) {
    console.warn("Нет сети, используем кэш");
    const cachedData = await getFromCache<T>(key);
    if (cachedData) {
      return cachedData;
    }
    throw new Error("Нет подключения к интернету и данных в кэше");
  }

  try {
    const data = await fetchFn();
    await setToCache(key, data, expiryTime);
    return data;
  } catch (error) {
    console.warn("Ошибка сети, пытаемся использовать кэш:", error);
    const cachedData = await getFromCache<T>(key);
    if (cachedData) {
      return cachedData;
    }
    throw error;
  }
};

export const cacheFirst = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
  expiryTime?: number,
): Promise<T> => {
  const cachedData = await getFromCache<T>(key);
  if (cachedData) {
    return cachedData;
  }

  const data = await fetchFn();
  await setToCache(key, data, expiryTime);
  return data;
};

export const staleWhileRevalidate = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
  expiryTime?: number,
): Promise<T> => {
  const cachedData = await getFromCache<T>(key);

  if (!cachedData) {
    const data = await fetchFn();
    await setToCache(key, data, expiryTime);
    return data;
  }

  try {
    const timestamp = await espoAppDB.getTimestamp(key);
    if (timestamp) {
      const age = Date.now() - timestamp;
      const STALE_THRESHOLD = 5 * 60 * 1000; // 5 минут
      if (age < STALE_THRESHOLD) {
        return cachedData;
      }
    }
  } catch (e) {}

  fetchFn()
    .then((data) => setToCache(key, data, expiryTime))
    .catch((error) => console.warn("Ошибка при обновлении кэша:", error));

  return cachedData;
};

export const invalidateListCache = async (
  entityType?: string,
): Promise<void> => {
  const allKeys = await espoAppDB.getAllKeys();

  const entityPatterns: Record<string, string[]> = {
    tasks: ["Task/list", "tasks:list"],
    meetings: ["Meeting/list", "meetings:list"],
    projects: ["Project/list", "projects:list"],
    opportunities: ["Opportunity", "opportunities:list"],
  };

  if (entityType) {
    const patterns = entityPatterns[entityType] || [
      `${entityType}:list`,
      `/${entityType}/list`,
    ];
    const keysToInvalidate = allKeys.filter((key) => {
      const keyLower = key.toLowerCase();
      return patterns.some((pattern) =>
        keyLower.includes(pattern.toLowerCase()),
      );
    });

    await Promise.all(keysToInvalidate.map((key) => espoAppDB.delete(key)));
  } else {
    const allPatterns = Object.values(entityPatterns).flat();
    const keysToInvalidate = allKeys.filter((key) => {
      const keyLower = key.toLowerCase();
      return allPatterns.some((pattern) =>
        keyLower.includes(pattern.toLowerCase()),
      );
    });

    await Promise.all(keysToInvalidate.map((key) => espoAppDB.delete(key)));
  }
};
